import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, Thermometer, Wind, Pencil } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * 30-second readiness check (BW18.5 PR-5.B).
 *
 * Optional, persists to public.daily_check_in. The v2 Recovery formula
 * reads from this table when present and otherwise defaults subjective
 * readiness to 70/100. The illness-flag fields drive the recovery
 * override (cap at 50) per the central design rule.
 */

const todayIso = (): string => new Date().toISOString().slice(0, 10);

type CheckInRow = {
  id: string;
  date: string;
  subjective_readiness: number | null;
  subjective_soreness: number | null;
  elevated_resp_rate: boolean | null;
  elevated_wrist_temp: boolean | null;
  fever_or_sore_throat: boolean | null;
};

export function DailyCheckInCard() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({
    queryKey: ["daily_check_in", userId, todayIso()],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<CheckInRow | null> => {
      if (!userId) return null;
      const { data } = await supabase
        .from("daily_check_in")
        .select(
          "id, date, subjective_readiness, subjective_soreness, elevated_resp_rate, elevated_wrist_temp, fever_or_sore_throat",
        )
        .eq("user_id", userId)
        .eq("date", todayIso())
        .maybeSingle();
      return (data as CheckInRow | null) ?? null;
    },
  });

  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: async (patch: Partial<CheckInRow>) => {
      if (!userId) throw new Error("Not signed in");
      const row = {
        user_id: userId,
        date: todayIso(),
        subjective_readiness: patch.subjective_readiness ?? null,
        subjective_soreness: patch.subjective_soreness ?? null,
        elevated_resp_rate: patch.elevated_resp_rate ?? false,
        elevated_wrist_temp: patch.elevated_wrist_temp ?? false,
        fever_or_sore_throat: patch.fever_or_sore_throat ?? false,
      };
      const { error } = await supabase
        .from("daily_check_in")
        .upsert(row, { onConflict: "user_id,date" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily_check_in", userId] });
      qc.invalidateQueries({ queryKey: ["today_recovery", userId] });
      toast.show("Readiness saved.");
      setEditing(false);
    },
    onError: (e) => toast.show(`Save failed, ${formatError(e)}`),
  });

  const existing = q.data ?? null;

  if (!editing && existing == null) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        style={{
          width: "100%",
          padding: "var(--space-3) var(--space-4)",
          background: "var(--bg-2)",
          border: "1px dashed var(--hair-2)",
          borderRadius: "var(--radius-card)",
          color: "var(--fg-2)",
          fontSize: "var(--type-body-s)",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <ClipboardCheck size={16} strokeWidth={1.5} color="var(--accent)" />
          30-second readiness check
        </span>
        <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          improves Recovery accuracy
        </span>
      </button>
    );
  }

  if (!editing && existing) {
    return (
      <div
        style={{
          padding: "var(--space-3) var(--space-4)",
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <span>
          <ClipboardCheck
            size={14}
            strokeWidth={1.6}
            color="var(--accent)"
            style={{ marginRight: 6, verticalAlign: "-2px" }}
          />
          Readiness logged · {existing.subjective_readiness ?? "—"}/5
          {existing.subjective_soreness != null && ` · soreness ${existing.subjective_soreness}/5`}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
          aria-label="Edit readiness"
        >
          <Pencil size={12} strokeWidth={1.6} /> Edit
        </button>
      </div>
    );
  }

  return (
    <CheckInForm
      initial={existing}
      onCancel={() => setEditing(false)}
      onSave={(patch) => save.mutate(patch)}
      saving={save.isPending}
    />
  );
}

function CheckInForm({
  initial,
  onCancel,
  onSave,
  saving,
}: {
  initial: CheckInRow | null;
  onCancel: () => void;
  onSave: (patch: Partial<CheckInRow>) => void;
  saving: boolean;
}) {
  const [readiness, setReadiness] = useState<number | null>(
    initial?.subjective_readiness ?? null,
  );
  const [soreness, setSoreness] = useState<number | null>(
    initial?.subjective_soreness ?? null,
  );
  const [respRate, setRespRate] = useState(Boolean(initial?.elevated_resp_rate));
  const [wristTemp, setWristTemp] = useState(Boolean(initial?.elevated_wrist_temp));
  const [feverSore, setFeverSore] = useState(Boolean(initial?.fever_or_sore_throat));

  return (
    <section
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          color: "var(--fg)",
          fontSize: "var(--type-body)",
          fontWeight: 500,
        }}
      >
        <ClipboardCheck size={16} strokeWidth={1.5} color="var(--accent)" />
        30-second readiness check
      </header>
      <Likert
        label="Readiness"
        value={readiness}
        onChange={setReadiness}
        anchorLow="drained"
        anchorHigh="excellent"
      />
      <Likert
        label="Soreness"
        value={soreness}
        onChange={setSoreness}
        anchorLow="none"
        anchorHigh="severe"
      />
      <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
        <legend
          style={{
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Illness flags · optional
        </legend>
        <FlagRow
          Icon={Wind}
          label="Elevated breathing rate at rest"
          checked={respRate}
          onChange={setRespRate}
        />
        <FlagRow
          Icon={Thermometer}
          label="Elevated wrist temperature"
          checked={wristTemp}
          onChange={setWristTemp}
        />
        <FlagRow
          Icon={Thermometer}
          label="Fever or sore throat"
          checked={feverSore}
          onChange={setFeverSore}
        />
      </fieldset>
      <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            height: 32,
            padding: "0 12px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            onSave({
              subjective_readiness: readiness,
              subjective_soreness: soreness,
              elevated_resp_rate: respRate,
              elevated_wrist_temp: wristTemp,
              fever_or_sore_throat: feverSore,
            })
          }
          style={{
            height: 32,
            padding: "0 14px",
            background: "var(--accent)",
            border: "none",
            borderRadius: "var(--radius-card)",
            color: "#0a0b0d",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}

function Likert({
  label,
  value,
  onChange,
  anchorLow,
  anchorHigh,
}: {
  label: string;
  value: number | null;
  onChange: (v: number) => void;
  anchorLow: string;
  anchorHigh: string;
}) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-pressed={active}
              style={{
                padding: "8px 0",
                background: active ? "var(--chip-hi)" : "transparent",
                color: active ? "var(--fg)" : "var(--fg-3)",
                border: `1px solid ${active ? "var(--accent-dim)" : "var(--hair-2)"}`,
                borderRadius: "var(--radius-chip)",
                fontSize: "var(--type-body-s)",
                cursor: "pointer",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
        }}
      >
        <span>{anchorLow}</span>
        <span>{anchorHigh}</span>
      </div>
    </div>
  );
}

function FlagRow({
  Icon,
  label,
  checked,
  onChange,
}: {
  Icon: typeof Thermometer;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        background: checked ? "var(--accent-wash)" : "transparent",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        cursor: "pointer",
        fontSize: "var(--type-body-s)",
        color: "var(--fg)",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: "var(--accent)" }}
      />
      <Icon size={14} strokeWidth={1.6} color="var(--fg-3)" />
      {label}
    </label>
  );
}
